import { makeVar, useReactiveVar } from '@apollo/client'
import { isMultilangPocEnabled } from '../../constants/featureFlags'
import { useHasStoredTranslations } from '../../modules/Translations/hooks/useHasStoredTranslations'
import { useSurveyId } from '../useSurveyId'

// Per-survey, so ticking one survey doesn't surface the Translations tab on
// every other survey in the browser.
const storageKey = (surveyId: string) =>
  `singleMarketNeedsTranslation:${surveyId}`

// Only a `true` declaration is ever worth persisting (see setNeedsTranslation
// below), so anything else stored — absent, 'false', or a stray legacy value —
// reads as "no declaration".
const readPersisted = (surveyId: string): boolean =>
  localStorage.getItem(storageKey(surveyId)) === 'true'

// Declarations made in this session, keyed by surveyId. localStorage is the
// persistence layer but isn't reactive, so this var is what re-renders the
// breadcrumb, the route and the launch gate the moment the checkbox flips —
// otherwise the tab would only appear after a reload. A survey missing from
// the map falls back to whatever localStorage holds.
export const needsTranslationVar = makeVar<
  Readonly<Record<string, boolean | undefined>>
>({})

const useNeedsTranslationVar = () => {
  const surveyId = useSurveyId()
  const session = useReactiveVar(needsTranslationVar)
  const hasStoredTranslations = useHasStoredTranslations()
  const declared = session[surveyId] ?? readPersisted(surveyId)

  return {
    // Stored evidence always wins over a declaration once it exists: this is
    // server-side, the same for every user and every browser, so a `false`
    // declaration can never suppress the tab/gate for a survey that genuinely
    // has translations sitting in it — a colleague who never saw the checkbox
    // still gets gated correctly. `declared` only matters while there's no
    // evidence yet, to pre-declare intent to translate before adding any.
    needsTranslation: hasStoredTranslations || declared,
    hasStoredTranslations,
    setNeedsTranslation: (value: boolean) => {
      // A `false` is never worth writing: absent evidence it's already the
      // default, and once evidence exists it can't take effect (see
      // needsTranslation above) — it would just be dead storage. So the only
      // state worth persisting is `true`, made while there's no evidence yet.
      if (value && !hasStoredTranslations) {
        localStorage.setItem(storageKey(surveyId), 'true')
      } else {
        localStorage.removeItem(storageKey(surveyId))
      }
      needsTranslationVar({ ...needsTranslationVar(), [surveyId]: value })
    }
  }
}

// The single predicate behind the Translations tab, its route and its
// launch-blocking completeness check: MultiLanguage feature access AND this
// survey needing translation — declared by the researcher, or forced on by
// translations already stored against it (see needsTranslation above; the
// latter always wins). Replaces the `showTranslations` localStorage backdoor
// from PR #1966.
export const useSingleMarketTranslationsEnabled = (): boolean => {
  const { needsTranslation } = useNeedsTranslationVar()

  return isMultilangPocEnabled() && needsTranslation
}

export default useNeedsTranslationVar
